import React, { useEffect, useMemo } from "react";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { classNames } from "primereact/utils";
import { Controller, useForm } from "react-hook-form";
import { Col, Container, Row } from "react-bootstrap";
import EzControlledInput from "../../../common/EzForm/EzControlledInput";
import EzControlledDropDowntWithFloatingLabel from "../../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import {
  currencys,
  defaultRevenues,
  defaultTaxes,
  countrys,
  confirmation,
} from "./data.json";
import { getFormBaseValidationRule } from "../../../utils/form/form-utils";
import Autosuggestion from "../../../common/Autosuggestion";
import { validateEmail } from "../../../utils/validation/email";

const UpdateCustomerComponent = (props) => {
  const {
    navigateToCustomerSummaryScreen,
    formFieldValueMap,
    isLoading,
    isCustomerDetailSuccess,
    isEditCustomerSuccess,
    editCustomer,
    customerCurrenciesProviderDD,
    customerCountriesProviderDD,
    isEdit,
    taxTypeDD,
    revenueTypeDD,
  } = props;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: useMemo(() => {
      return formFieldValueMap;
    }, [formFieldValueMap]),
  });

  useEffect(() => {
    if (isCustomerDetailSuccess || isEditCustomerSuccess) {
      reset({
        ...formFieldValueMap,
      });
    }
  }, [isCustomerDetailSuccess, isEditCustomerSuccess]);

  const isBrokerValue = watch("broker");

  const onSubmit = (data) => {    
    if (isEdit) {
      editCustomer(data);
    }
  };

  const renderDividerRowWithTime = (label, align = "top", type = "solid") => {
    return (
      <Row>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <div>
            <b className="ml-2">{label || ""}</b>
          </div>
          <div>
            <b className="mr-2">
              <Button
                tooltip={"Back to Customer List"}
                tooltipOptions={{ position: "left" }}
                label={"Back to Customer List"}
                className={`p-button`}
                onClick={navigateToCustomerSummaryScreen}
              />
            </b>
          </div>
        </div>
        <Divider align={align} type={type} />
      </Row>
    );
  };

  return (
    <div className="paddingbox">
      <div
        className={classNames("containerboxform", { "p-disabled": isLoading })}
      >
        <div className="form-demo">
          <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <Container fluid className="card-box">
              {renderDividerRowWithTime("Update Customer")}

              <Row className="gap-3 g-4">
                <Col md={6}>
                  <EzControlledInput
                    name="name"
                    control={control}
                    label="Name"
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    isError={!!errors.name}
                    errorMsg={errors.name?.message}
                  />
                </Col>

                <Col md={6}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="currency_id"
                    control={control}
                    label="Currency"
                    optionLabel="value"
                    optionValue="label"
                    options={customerCurrenciesProviderDD}
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    isError={!!errors.currency}
                    errorMsg={errors.currency?.message}
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="email"
                    control={control}
                    label="Email"
                    rules={{
                      ...getFormBaseValidationRule().DEFAULT.VALIDATION_RULE,
                      validate: (value) => validateEmail(value) || "Invalid email address",
                    }}
                    isError={!!errors.email}
                    errorMsg={errors.email?.message}
                  />
                </Col>
                <Col md={6}>
                  <EzControlledInput
                    name="secondary_email_id"
                    control={control}
                    label="Secondary Email"
                    type="email"
                    rules={{
                      validate: (value) =>
                        !value ||
                        validateEmail(value) ||
                        "Invalid email address",
                    }}
                    errorMsg={errors.secondary_email_id?.message}
                    isError={!!errors.secondary_email_id}
                  />
                </Col>
                <Col md={6}>
                  <EzControlledInput
                    name="site_url"
                    control={control}
                    label="Web URL"
                    isError={!!errors.webUrl}
                    errorMsg={errors.webUrl?.message}
                  />
                </Col>
                <Divider />
                <Col md={6}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="broker"
                    control={control}
                    label="Is Broker?"
                    optionLabel="label"
                    optionValue="value"
                    options={confirmation}
                  />
                </Col>

                <Col md={6}>
                  <label htmlFor="broker_ids" className="form-label">
                    Broker
                  </label>
                  <Controller
                    name="broker_ids"
                    control={control}
                    render={({ field }) => (
                      <Autosuggestion
                        value={field.value}
                        name={field.name}
                        onChange={field.onChange}
                        disabled={isBrokerValue}
                        placeholder="Search Broker..."
                        // getAutosuggest={}
                        onClick={(value, label) => field.onChange(value)}
                      />
                    )}
                  />
                </Col>
                <Divider />
                <Col md={6}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="revenue_id"
                    control={control}
                    label="Default Revenue"
                    optionLabel="value"
                    optionValue="label"
                    options={revenueTypeDD}
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="rate"
                    control={control}
                    label="Rate"
                  />
                </Col>

                <Col md={6}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="tax_id"
                    control={control}
                    label="Default Tax"
                    optionLabel="value"
                    optionValue="label"
                    options={taxTypeDD}
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="due_days"
                    control={control}
                    label="Due Days"
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="cont_name"
                    control={control}
                    label="Contact Person"
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="phone"
                    control={control}
                    label="Phone Number"
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="phone2"
                    control={control}
                    label="Alternate Number"
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="info"
                    control={control}
                    label="Other Information"
                  />
                </Col>
                <Divider />

                <Col md={6}>
                  <EzControlledInput
                    name="address1"
                    control={control}
                    label="Billing Address"
                    rules={getFormBaseValidationRule().DEFAULT.VALIDATION_RULE}
                    isError={!!errors.billingAddress}
                    errorMsg={errors.billingAddress?.message}
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="address2"
                    control={control}
                    label="Shipping Address"
                  />
                </Col>

                <Col md={3}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="billing_country"
                    control={control}
                    label="Select Country"
                    optionLabel="label"
                    optionValue="value"
                    options={customerCountriesProviderDD}
                  />
                </Col>
                <Col md={3}>
                  <EzControlledInput
                    name="billing_state"
                    control={control}
                    label="Billing State"
                  />
                </Col>
                <Col md={3}>
                  <EzControlledDropDowntWithFloatingLabel
                    name="shipping_country"
                    control={control}
                    label="Select Country"
                    optionLabel="label"
                    optionValue="value"
                    options={customerCountriesProviderDD}
                  />
                </Col>
                <Col md={3}>
                  <EzControlledInput
                    name="shipping_state"
                    control={control}
                    label="Shipping State"
                  />
                </Col>

                <Col md={3}>
                  <EzControlledInput
                    name="billing_city"
                    control={control}
                    label="Billing City"
                  />
                </Col>
                <Col md={3}>
                  <EzControlledInput
                    name="billing_zip_code"
                    control={control}
                    label="Zip/Postal Code"
                  />
                </Col>
                <Col md={3}>
                  <EzControlledInput
                    name="shipping_city"
                    control={control}
                    label="Shipping City"
                  />
                </Col>
                <Col md={3}>
                  <EzControlledInput
                    name="shipping_zip_code"
                    control={control}
                    label="Zip/Postal Code"
                  />
                </Col>

                <Col md={6}>
                  <EzControlledInput
                    name="tax_no"
                    control={control}
                    label="Tax Number"
                  />
                </Col>
                <Col md={6}>
                  <EzControlledInput
                    name="crm_id"
                    control={control}
                    label="CRM ID"
                  />
                </Col>
              </Row>

              <div
                style={{ display: "flex", justifyContent: "end", gap: "8px" }}
              >
                <Button
                  label="Cancel"
                  className="p-button-danger p-button-outlined"
                  type="button"
                  style={{ width: "90px" }}
                  onClick={navigateToCustomerSummaryScreen}
                />
                {/* <Button
                  label="Reset"
                  className="p-button-warning"
                  type="reset"
                  style={{ width: "90px" }}
                /> */}
                <Button
                  label="Update"
                  className="p-button-success p-button-raised"
                  type="submit"
                  style={{ width: "90px" }}
                />
              </div>
            </Container>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateCustomerComponent;
